import { Checkbox, FormControlLabel, styled, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip } from "@mui/material"
import React, { useEffect, useMemo, useState } from "react"
import { useFormConfigContext } from "src/components/era-package/forms/hooks/useFormConfig"

const StyledRow = styled(TableRow)(({ theme }) => ({
  '& > td:last-child': {
    borderLeft: `1px solid ${theme.palette.divider}`
  },
}))

export const PGACorrection = ({ formData, setFormData, optionsMap, readOnly, lineItems }) => {
  const [selectedPGAs, setSelectedPGAs] = useState({})
  const [removedPGAs,  setRemovedPGAs]  = useState({})
  const rhf = useFormConfigContext() || null

  useEffect(() => {
    if (rhf) {
      rhf.register('pgaCorrections', { value: [] })
      rhf.register('pgaRemovals',    { value: [] })
    }
  }, [])

  const rows = useMemo(() => {
    const items = Array.isArray(lineItems) ? lineItems : (lineItems?.items ?? [])
    return items
      .filter(item => item.pgaList && item.pgaList.length > 0)
      .map(item => ({
        esLine:  item.esLine,
        hts:     item.tariff1 || '',
        pgaList: item.pgaList.map(pga => ({
          id:   `${item.esLine}-${pga.agencyCode || ''}${pga.programCode || ''}`,
          name: `${pga.agencyCode || ''}-${pga.programCode || ''}`
        }))
      }))
  }, [lineItems])

  useEffect(() => {
    const corrections = Object.keys(selectedPGAs)
    const removals    = Object.keys(removedPGAs)
    if (typeof setFormData === 'function')
      setFormData(prev => ({ ...prev, pgaCorrections: corrections, pgaRemovals: removals }))
    if (rhf) {
      rhf.setValue('pgaCorrections', corrections)
      rhf.setValue('pgaRemovals',    removals)
    }
  }, [selectedPGAs, removedPGAs])

  const toggleCorrection = (key) => {
    setSelectedPGAs(prev => {
      const next = { ...prev }
      if (next[key]) { delete next[key] } else { next[key] = true }
      return next
    })
    setRemovedPGAs(prev => { const next = { ...prev }; delete next[key]; return next })
  }

  const toggleRemoval = (key) => {
    setRemovedPGAs(prev => {
      const next = { ...prev }
      if (next[key]) { delete next[key] } else { next[key] = true }
      return next
    })
    setSelectedPGAs(prev => { const next = { ...prev }; delete next[key]; return next })
  }

  const toggleRowRemoval = (pgaList, checked) => {
    if (checked) {
      setRemovedPGAs(prev => { const next = { ...prev }; pgaList.forEach(p => { next[p.id] = true }); return next })
      setSelectedPGAs(prev => { const next = { ...prev }; pgaList.forEach(p => { delete next[p.id] }); return next })
    } else {
      setRemovedPGAs(prev => { const next = { ...prev }; pgaList.forEach(p => { delete next[p.id] }); return next })
    }
  }

  const pgaKeys = useMemo(() => rows.flatMap(r => r.pgaList.map(p => p.id)), [rows])
  const allCorrSelected = pgaKeys.length > 0 && pgaKeys.every(k => !!selectedPGAs[k])
  const partialCorr     = pgaKeys.some(k => !!selectedPGAs[k]) && !allCorrSelected

  const handleSelectAll = (checked) => {
    if (!checked) { setSelectedPGAs({}); return }
    const next = {}
    pgaKeys.forEach(k => { next[k] = true })
    setSelectedPGAs(next)
    setRemovedPGAs({})
  }

  return <TableContainer><Table sx={{ tableLayout: 'fixed' }}>
    <TableHead><TableRow>
      <TableCell sx={{ width: '8vw',  minWidth: 100 }}>Entry Line</TableCell>
      <TableCell sx={{ width: '14vw', minWidth: 180 }}>HTS</TableCell>
      <TableCell padding='checkbox' sx={{ width: 'auto' }}>
        <Tooltip title='Select All PGAs for Correction'>
          <FormControlLabel control={<Checkbox
            checked={allCorrSelected}
            indeterminate={partialCorr}
            onChange={e => handleSelectAll(e.target.checked)}
            disabled={readOnly}
          />} label='PGA' disableTypography sx={{ ml: 0 }} />
        </Tooltip>
      </TableCell>
      <TableCell sx={{ width: '10vw', minWidth: 130 }}>Remove PGA</TableCell>
    </TableRow></TableHead>
    <TableBody>
      {rows.length === 0 && (
        <TableRow><TableCell colSpan={4} sx={{ color: 'text.secondary', py: 2 }}>
          No PGA line items found for this entry.
        </TableCell></TableRow>
      )}
      {rows.map((row, ri) => <React.Fragment key={row.esLine}>
        <StyledRow>
          <TableCell rowSpan={row.pgaList.length} sx={ri === rows.length - 1 ? { borderBottom: 0 } : {}}>{row.esLine}</TableCell>
          <TableCell rowSpan={row.pgaList.length} sx={ri === rows.length - 1 ? { borderBottom: 0 } : {}}>{row.hts}</TableCell>
          <TableCell padding='checkbox'>
            <FormControlLabel control={<Checkbox
              checked={!!selectedPGAs[row.pgaList[0].id]}
              onChange={() => toggleCorrection(row.pgaList[0].id)}
              disabled={readOnly}
            />} label={row.pgaList[0].name} disableTypography sx={{ ml: 0 }} />
          </TableCell>
          <TableCell rowSpan={row.pgaList.length} sx={ri === rows.length - 1 ? { borderBottom: 0 } : {}}>
            <FormControlLabel control={<Checkbox
              checked={row.pgaList.every(p => !!removedPGAs[p.id])}
              indeterminate={row.pgaList.some(p => !!removedPGAs[p.id]) && !row.pgaList.every(p => !!removedPGAs[p.id])}
              onChange={e => toggleRowRemoval(row.pgaList, e.target.checked)}
              disabled={readOnly}
            />} label='Remove PGA' disableTypography sx={{ ml: 0 }} />
          </TableCell>
        </StyledRow>
        {row.pgaList.slice(1).map(pga => <StyledRow key={pga.id}>
          <TableCell padding='checkbox'>
            <FormControlLabel control={<Checkbox
              checked={!!selectedPGAs[pga.id]}
              onChange={() => toggleCorrection(pga.id)}
              disabled={readOnly}
            />} label={pga.name} disableTypography sx={{ ml: 0 }} />
          </TableCell>
        </StyledRow>)}
      </React.Fragment>)}
    </TableBody>
  </Table></TableContainer>
}